/**
 * RFI Messages API Client
 *
 * GW-3056: Client for gw-services-api RFI messaging endpoints.
 * Base URL: services.{env}.generalwisdom.com
 * All requests include Accept-Version: 1.0 header via ServicesApiClient.
 */

import type { RFIMessage, RFIMessageListResponse } from '@/types/rfi-message.types';

import { ServicesApiClient } from './servicesApiClient';

class RFIMessagesAPI extends ServicesApiClient {
  // GW-9566: Suppress the global session-expired logout for this client
  // (same precedent as GW-8271 applicationsApi). The org RFI detail page
  // (OrganizationRFIDetailPage) fetches GET /rfis/:id (200, renders) AND
  // GET /rfis/:id/messages in parallel. For an org_admin who is authenticated
  // but not a participant, the messages sub-fetch returns 403 {"error":"not a
  // participant in this RFI"}. Without this flag that 403 tripped the
  // requiresAuth 401/403 → authEvents.emit('session-expired') path in
  // baseApiClient, hard-redirecting to /auth/login before the RFI title could
  // paint. With suppressSessionExpired, the 403 throws ApiError to the caller
  // so the messages panel degrades in-context while the RFI detail still
  // renders. Genuine session expiry is still caught by the sibling requiresAuth
  // clients (e.g. rfis.ts) and getAuthToken()'s refresh-failure path.
  constructor() {
    super({ suppressSessionExpired: true });
  }

  /**
   * Send a message in an RFI thread.
   * POST /rfis/:rfiId/messages
   */
  async sendMessage(rfiId: string, content: string): Promise<RFIMessage> {
    return this.post<RFIMessage>(`/rfis/${rfiId}/messages`, { content });
  }

  /**
   * List messages for an RFI.
   * GET /rfis/:rfiId/messages
   */
  async listMessages(
    rfiId: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<RFIMessageListResponse> {
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<RFIMessageListResponse>(`/rfis/${rfiId}/messages${qs}`);
  }

  /**
   * Mark a message as read.
   * PUT /rfis/:rfiId/messages/:messageId/read
   */
  async markRead(rfiId: string, messageId: string): Promise<void> {
    return this.put<void>(`/rfis/${rfiId}/messages/${messageId}/read`, {});
  }
}

export const rfiMessagesApi = new RFIMessagesAPI();
